import { useQuery } from '@apollo/client';
import { DataArray } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';
import { indexBy, prop } from 'ramda';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router';

import { PageError } from '~/components/PageError/PageError';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useAuthRoles } from '~/hooks/useAuthRoles/useAuthRoles';
import { CMDI_NEEDED_ROLES, useCmdiHasuraContext } from '~/hooks/useCmdiHasuraContext/useCmdiHasuraContext';

import { CMDI_META_COLUMN_CONFIG_QUERY } from './CmdiMetaColumnConfigQuery.gql';
import { CMDI_META_QUERY } from './CmdiMetaQuery.gql';
import { CmdiDataGridWidget } from './components/CmdiDataGridWidget/CmdiDataGridWidget';
import { CmdiPageTitle } from './components/CmdiPageTitle/CmdiPageTitle';

export const Cmdi = () => {
  const { entityId } = useParams<'entityId'>();
  const { hasAnyRole } = useAuthRoles();
  const { hasPermission } = usePermissions();

  const hasNeededRole = hasAnyRole(CMDI_NEEDED_ROLES);
  const context = useCmdiHasuraContext();
  const { data, loading } = useQuery(CMDI_META_QUERY, {
    context,
    skip: !hasNeededRole,
  });

  const { data: columnConfigData, loading: columnConfigLoading } = useQuery(CMDI_META_COLUMN_CONFIG_QUERY, {
    skip: !hasNeededRole || !entityId,
    variables: {
      id: entityId || '',
    },
    context,
  });

  const entities = data?.cmdi.meta_entity;

  const entitiesFiltered = useMemo(
    () =>
      entities?.filter((item) => {
        return item.needsPermission ? hasPermission(item.needsPermission as Permission) : true;
      }),
    [entities, hasPermission],
  );

  const entitiesById = useMemo(() => {
    return entities ? indexBy(prop('id'), entities) : {};
  }, [entities]);

  const entity = entityId ? entitiesById[entityId] : undefined;
  const entityName = entity?.name;
  const entityNeedsPermission = entity?.needsPermission;
  const entityColumnConfig = columnConfigData?.cmdi.meta_columnConfig;
  const hasEntityPermission = entityNeedsPermission ? hasPermission(entityNeedsPermission as Permission) : true;

  let errorContent: string | null = null;

  if (!hasNeededRole) {
    errorContent = 'Cmdi.error.role';
  } else if (!hasEntityPermission) {
    errorContent = 'Cmdi.error.permission';
  } else if (entitiesFiltered && entitiesFiltered.length === 0) {
    errorContent = 'Cmdi.error.empty';
  }

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <PageHeaderStatic
          title={<CmdiPageTitle selectedEntity={entity} />}
          maxWidth={false}
          icon={<WidgetIcon icon={<DataArray fontSize="small" />} />}
          left={loading || columnConfigLoading ? <CircularProgress size={20} /> : undefined}
        />
      }
    >
      {errorContent ? (
        <PageError>
          <Trans i18nKey={errorContent} />
        </PageError>
      ) : entityName && entityColumnConfig && hasEntityPermission ? (
        <CmdiDataGridWidget key={entityId} entityName={entityName} columnConfig={entityColumnConfig} />
      ) : null}
    </PageLayout>
  );
};
